import Phaser from 'phaser';
import BattleStateManager from '../game/BattleStateManager.js';

/**
 * FirstTimeScene - Welcome screen for new users
 * 
 * This scene displays after the victory screen for 10 seconds.
 * It shows a welcome message for new users (account age <= 1 hour)
 * and encourages account creation.
 * 
 * @class FirstTimeScene
 */
export class FirstTimeScene extends Phaser.Scene {
  constructor() {
    super({ key: 'FirstTimeScene' });
    this.displayDuration = 8000; // 10 seconds
    this.startTime = null;
    this.newUsers = [];
    this.battleResults = null;
  }

  async init(data) {
    console.log('👋 FirstTimeScene: init() called with data:', data);
    
    // Get battle results from scene data or BattleStateManager
    this.battleResults = data?.battleResults || BattleStateManager.getBattleResults() || {};
    this.winners = data?.winners || BattleStateManager.getWinners() || [];
    this.allPlayers = data?.allPlayers || BattleStateManager.getPlayerList() || [];
    this.battleRewards = data?.battleRewards || BattleStateManager.getBattleRewards() || null;
    
    // Fetch new users (account age <= 1 hour)
    //await this.fetchNewUsers();
    
    console.log('👋 FirstTimeScene initialized:', {
      newUsersCount: this.newUsers.length,
      totalPlayers: this.allPlayers.length
    });
  }

  async fetchNewUsers() {
    try {
      // Get all unique user IDs and wallet addresses from battle results
      const playerData = this.battleResults?.playerRankings || [];
      const userIds = new Set();
      const walletAddresses = new Set();
      
      playerData.forEach(ranking => {
        const player = ranking.player;
        if (player?.userId) {
          userIds.add(player.userId);
        }
        if (player?.walletAddress) {
          walletAddresses.add(player.walletAddress);
        }
        if (player?.wallet) {
          walletAddresses.add(player.wallet);
        }
      });
      
      // Fetch new users from server
      // For now, we'll check if we have user IDs, otherwise we'll use wallet addresses
      if (userIds.size > 0 || walletAddresses.size > 0) {
        //add api key to the headers
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.API_KEY}`
        };
        const response = await fetch('/api/users/new-users', {
          method: 'POST',
          body: JSON.stringify({
            userIds: Array.from(userIds),
            walletAddresses: Array.from(walletAddresses)
          }),
          headers: headers
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.newUsers) {
            this.newUsers = data.newUsers.slice(0, 10); // Limit to 10
            console.log('👋 Fetched new users:', this.newUsers);
          } else {
            console.warn('👋 No new users found in response, using fallback');
            this.newUsers = this.getFallbackNewUsers();
          }
        } else {
          console.warn('👋 Failed to fetch new users, using fallback');
          this.newUsers = this.getFallbackNewUsers();
        }
      } else {
        // Fallback: use player names if we can't determine account age
        this.newUsers = this.getFallbackNewUsers();
      }
    } catch (error) {
      console.warn('👋 Error fetching new users:', error);
      this.newUsers = this.getFallbackNewUsers();
    }
  }

  getFallbackNewUsers() {
    // Fallback: return empty array or use player data if available
    // This will be replaced once server endpoint is implemented
    const playerData = this.battleResults?.playerRankings || [];
    return playerData
      .slice(0, 10)
      .map(ranking => ({
        name: ranking.player?.name || ranking.player?.hero?.name || 'Player',
        userId: ranking.player?.userId || null,
        walletAddress: ranking.player?.walletAddress || ranking.player?.wallet || null
      }));
  }

  create() {
    console.log('👋 FirstTimeScene: create() called');
    const { width, height } = this.cameras.main;
    
    // Create dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    this.background.setAlpha(0.95);
    
    // Add subtle gradient overlay
    this.gradientOverlay = this.add.rectangle(width / 2, height / 2, width, height, 0x1E293B);
    this.gradientOverlay.setAlpha(0.3);
    
    // Create main content container
    this.contentContainer = this.add.container(width / 2, height / 2).setDepth(10);
    
    // Create welcome UI
    this.createWelcomeUI(width, height);
    
    // Start the display timer (delayed by 1 second for fade-in)
    this.startTime = Date.now() + 1000; // Add 1 second delay for fade-in
    console.log('👋 FirstTimeScene: startTime set to', this.startTime);
    
    // Fade in animation
    this.contentContainer.setAlpha(0);
    this.tweens.add({
      targets: this.contentContainer,
      alpha: 1,
      duration: 1000,
      ease: 'Power2'
    });
  }

  createWelcomeUI(width, height) {
    // Calculate responsive sizes for mobile
    const isMobile = false;
    const baseFontSize = 56;
    const headingFontSize = 72;
    const ctaFontSize = 48;
    
    // Main container dimensions
    const containerWidth = Math.min(1000, width * 1);
    const containerHeight = Math.min(700, height * 1);
    
    // Background panel with glow effect
    const panelBg = this.add.rectangle(0, 0, containerWidth, containerHeight, 0x1E293B);
    panelBg.setStrokeStyle(4, 0xF3F300);
    panelBg.setAlpha(0.95);
    this.contentContainer.add(panelBg);
    
    // Add glow effect
    const glow = this.add.rectangle(0, 0, containerWidth + 20, containerHeight + 20, 0xF3F300);
    glow.setAlpha(0.1);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(glow, 0);
    
    // "First Time?" heading at the top
    const heading = this.add.text(0, -containerHeight / 2 + 60, 'First Time?', {
      fontSize: `${headingFontSize}px`,
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5);
    this.contentContainer.add(heading);
    
    // Animate heading
    this.tweens.add({
      targets: heading,
      scaleX: { from: 0.9, to: 1.1 },
      scaleY: { from: 0.9, to: 1.1 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // "Welcome!" text
    const welcomeText = this.add.text(0, -containerHeight / 2 + 130, 'Welcome!', {
      fontSize: `${baseFontSize}px`,
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5);
    this.contentContainer.add(welcomeText);
    
    // CTA text right underneath Welcome
    const ctaY = -containerHeight / 2 + 300;
    const ctaText = this.add.text(0, ctaY, 
      'Your hero will now battle 24/7, earning XP, items, and tokens!',
      {
        fontSize: `${ctaFontSize + 10}px`,
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
        wordWrap: { width: containerWidth - 80 },
        align: 'center'
      }
    ).setOrigin(0.5).setScale(1.2);
    this.contentContainer.add(ctaText);
    
    //Start big and quickly shrink back to normal size
    this.tweens.add({
      targets: ctaText,
      scaleX: { from: 1.2, to: 1 },
      scaleY: { from: 1.2, to: 1 },
      duration: 250,
      delay: 0,
      repeat: 0,
      ease: 'Sine.easeIn'
    });

    const ctaText2 = this.add.text(0, ctaY + 170, 
      'Claim your new hero and prizes at:',
      {
        fontSize: `${ctaFontSize}px`,
        color: '#FFFF00',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
        wordWrap: { width: containerWidth - 80 },
        align: 'center'
      }
    ).setOrigin(0.5).setScale(1.2).setAlpha(0.0);
    this.contentContainer.add(ctaText2);
    
    // Animate CTA text to pop in after a second and stay, no yoyo, start big and quickly shrink back to normal size
    this.tweens.add({
      targets: ctaText2,
      scaleX: { from: 1.2, to: 1 },
      scaleY: { from: 1.2, to: 1 },
      alpha: { from: 0.0, to: 1 },
      duration: 250,
      delay: 250,
      repeat: 0,
      ease: 'Sine.easeIn'
    });

//add a large white rectangle with the text "soulwars.gg" in the center
const soulwarsText = this.add.text(0, ctaY + 260, "soulwars.gg", {
  fontSize: `${ctaFontSize + 50}px`,
  color: '#FFFFFF',
  fontFamily: 'Arial, sans-serif',
  fontStyle: 'bold',
  stroke: '#000000',
  strokeThickness: 2
});
soulwarsText.setOrigin(0.5);
soulwarsText.setScale(1.5);
soulwarsText.setAlpha(0.0);
this.contentContainer.add(soulwarsText);
this.tweens.add({
  targets: soulwarsText,
  alpha: { from: 0.0, to: 1 },
  duration: 250,
  delay: 500,
  repeat: 0,
  ease: 'Sine.easeIn'
});
 this.tweens.add({
   targets: soulwarsText,
   scaleX: { from: 1.5, to: 1 },
   scaleY: { from: 1.5, to: 1 },
   duration: 250,
   delay: 750,
   repeat: 0,
   ease: 'Sine.easeIn'
 });
     
    // New users row underneath CTA
    if (this.newUsers.length > 0) {
      //this.createNewUsersRow(containerWidth, containerHeight, false);
    }
    
    // Create countdown progress bar at the bottom
    this.createProgressBar(containerWidth, containerHeight);
  }
  
  createProgressBar(containerWidth, containerHeight) {
    const barWidth = containerWidth * 0.8;
    const barHeight = 20;
    const barY = containerHeight / 2 - 40;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(0, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarBg);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    this.progressBarFill = this.add.rectangle(-barWidth / 2, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarFill);
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(0, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(progressGlow, this.contentContainer.list.indexOf(this.progressBarBg));
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
  }

  createNewUsersRow(containerWidth, containerHeight, isMobile) {
    const userCount = Math.min(this.newUsers.length, 10);
    const maxSpacing = isMobile ? 80 : 120;
    const totalWidth = Math.min(userCount * maxSpacing, containerWidth - 100);
    const startX = -totalWidth / 2;
    const spacing = userCount > 1 ? totalWidth / (userCount - 1) : 0;
    // Position new users row underneath CTA (CTA is at -containerHeight/2 + 240, and CTA text is ~100px tall, so start users at ~360)
    const userY = -containerHeight / 2 + 360;
    
    // Player sprite scale
    const playerScale = isMobile ? 0.25 : 0.35;
    
    this.newUsers.slice(0, 10).forEach((user, index) => {
      const x = userCount === 1 ? 0 : startX + (index * spacing);
      
      // Create user container
      const userContainer = this.add.container(x, userY).setDepth(5);
      
      // Try to get player sprite
      let playerSprite = null;
      const playerData = this.battleResults?.playerRankings?.find(
        r => (r.player?.userId === user.userId) || 
             (r.player?.walletAddress === user.walletAddress) ||
             (r.player?.wallet === user.walletAddress)
      );
      
      if (playerData?.player) {
        const player = playerData.player;
        const textureKey = `player_${player.heroId || player.userId || player.walletAddress}`;
        
        if (this.textures.exists(textureKey)) {
          playerSprite = this.add.image(0, 0, textureKey);
          playerSprite.setScale(playerScale);
        }
      }
      
      // Fallback: create colored rectangle with initial
      if (!playerSprite) {
        const initial = (user.name || 'P').charAt(0).toUpperCase();
        const graphics = this.add.graphics();
        graphics.fillStyle(0x4F46E5);
        graphics.fillRoundedRect(-30, -30, 60, 60, 10);
        graphics.generateTexture(`newuser_${index}`, 60, 60);
        graphics.destroy();
        
        playerSprite = this.add.image(0, 0, `newuser_${index}`);
        playerSprite.setScale(playerScale);
        
        // Add initial text
        const initialText = this.add.text(0, 0, initial, {
          fontSize: isMobile ? '16px' : '20px',
          color: '#FFFFFF',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 2
        }).setOrigin(0.5);
        userContainer.add(initialText);
      }
      
      userContainer.add(playerSprite);
      
      // User name (positioned below sprite)
      const nameText = this.add.text(0, isMobile ? 50 : 60, user.name || 'Player', {
        fontSize: isMobile ? '14px' : '18px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5);
      userContainer.add(nameText);
      
      // Add glow effect for new users
      const glow = this.add.circle(0, 0, isMobile ? 35 : 45, 0x10B981);
      glow.setAlpha(0.2);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      userContainer.addAt(glow, 0);
      
      // Animate user appearance
      userContainer.setAlpha(0);
      userContainer.setScale(0);
      this.tweens.add({
        targets: userContainer,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 500,
        delay: index * 100,
        ease: 'Back.easeOut'
      });
      
      // Add subtle floating animation
      this.tweens.add({
        targets: userContainer,
        y: { from: userY - 5, to: userY + 5 },
        duration: 2000,
        delay: index * 100 + 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    });
  }

  update(time, delta) {
    if (!this.startTime) {
      return;
    }
    
    const elapsed = Date.now() - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const progress = Math.max(0, Math.min(1, remaining / this.displayDuration));
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    // Update progress bar
    if (this.progressBarFill && this.progressBarBg) {
      const barWidth = this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      this.progressBarFill.x = -barWidth / 2;
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
    }
    
    // Transition to WrapUpScene when time is up
    if (remaining <= 0) {
      console.log('📊 Transitioning to WrapUpScene from FirstTimeScene...');
      this.scene.start('WrapUpScene', {
        battleResults: this.battleResults,
        winners: this.winners,
        allPlayers: this.allPlayers,
        battleRewards: this.battleRewards
      });
    }
  }

  shutdown() {
    console.log('👋 FirstTimeScene shutdown');
  }
}

